import React, { useState, useEffect, useRef } from 'react';
import { Bell, ChevronLeft, ChevronRight, Gavel, CheckCircle, Clock, Users, IndianRupee, AlertTriangle, XCircle, Check, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import './BellNotification.css';

const BellNotification = ({ api }) => {
    const { socket } = useAuth();
    const [notifications, setNotifications] = useState(0);
    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const [notifList, setNotifList] = useState([]);
    const [loadingNotifs, setLoadingNotifs] = useState(false);
    const [viewAllMode, setViewAllMode] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const notifRef = useRef(null);

    // --- NEW: Action States ---
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [actionedNotifs, setActionedNotifs] = useState([]); // Tracks resolved requests to hide buttons
    const [infoModal, setInfoModal] = useState({ show: false, title: '', message: '', isError: false });

    useEffect(() => {
        api.getUnreadNotifications()
            .then(res => {
                if (res.data.success) setNotifications(res.data.data.count);
            })
            .catch(err => console.error("Failed to load notifications", err));
    }, [api]);

    useEffect(() => {
        if (!socket) return;
        const handleNewNotification = (data) => {
            setNotifications(prev => prev + 1);
        };
        socket.on("newNotification", handleNewNotification);
        return () => socket.off("newNotification", handleNewNotification);
    }, [socket]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            // Don't close if clicking inside the info modal overlay
            if (document.getElementById('notif-modal-overlay')?.contains(event.target)) {
                return;
            }
            if (notifRef.current && !notifRef.current.contains(event.target)) {
                setIsNotifOpen(false);
                setViewAllMode(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNotificationClick = async () => {
        const newOpenState = !isNotifOpen;
        setIsNotifOpen(newOpenState);

        if (newOpenState) {
            setViewAllMode(false);
            setLoadingNotifs(true);
            try {
                const response = await api.getNotificationsList(true, 5, 1);
                if (response.data.success) {
                    const fetchedNotifs = response.data.data.notifications;
                    setNotifList(fetchedNotifs);
                    setNotifications(prev => Math.max(0, prev - fetchedNotifs.length));
                }
            } catch (error) {
                console.error("Failed to fetch unread notifications", error);
            } finally {
                setLoadingNotifs(false);
            }
        }
    };

    const fetchAllNotifications = async (page = 1) => {
        setLoadingNotifs(true);
        setViewAllMode(true);
        try {
            const response = await api.getNotificationsList(false, 10, page);
            if (response.data.success) {
                setNotifList(response.data.data.notifications);
                setCurrentPage(response.data.data.pagination.page);
                setTotalPages(response.data.data.pagination.totalPages);
            }
        } catch (error) {
            console.error("Failed to fetch all notifications", error);
        } finally {
            setLoadingNotifs(false);
        }
    };

    // --- NEW: Handle Inline Approve/Reject ---
    const handleJoinAction = async (e, notif, actionType) => {
        e.stopPropagation(); // Prevents dropdown from closing
        if (!api.groups?.approveJoin) {
            alert("Approve/Reject API endpoints are missing in adminApi.js");
            return;
        }

        setActionLoadingId(notif._id);
        try {
            const payload = {
                userId: notif.metadata?.userId,
                notificationId: notif._id
            };

            let res;
            if (actionType === 'APPROVE') {
                res = await api.groups.approveJoin(notif.groupId, payload);
            } else {
                res = await api.groups.rejectJoin(notif.groupId, payload);
            }

            // Mark as actioned so buttons disappear instantly
            setActionedNotifs(prev => [...prev, notif._id]);

            // Show Success Modal
            setInfoModal({
                show: true,
                title: actionType === 'APPROVE' ? 'Approved!' : 'Rejected',
                message: res.data.message,
                isError: false
            });

        } catch (err) {
            setInfoModal({
                show: true,
                title: 'Action Failed',
                message: err.response?.data?.message || 'An error occurred while processing the request.',
                isError: true
            });
        } finally {
            setActionLoadingId(null);
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'GROUP_ACTIVATED': return <CheckCircle size={16} className="text-emerald" />;
            case 'BIDDING_OPEN': return <Gavel size={16} className="text-blue" />;
            case 'BIDDING_CLOSED': return <CheckCircle size={16} className="text-emerald" />;
            case 'PAYMENT_CONFIRMED': return <CheckCircle size={16} className="text-emerald" />;
            case 'PAYMENT_REMINDER': return <Clock size={16} className="text-amber" />;
            case 'GROUP_JOIN_REQUEST': return <Users size={16} className="text-blue" />;
            case 'PAYMENT_COLLECTION_REQUEST': return <IndianRupee size={16} className="text-emerald" />;
            case 'BIDDING_TIE': return <AlertTriangle size={16} className="text-rose" />;
            case 'FINALIZE_BLOCKED': return <AlertTriangle size={16} className="text-rose" />;
            default: return <Bell size={16} className="text-slate" />;
        }
    };

    return (
        <div className="notification-wrapper" ref={notifRef}>
            <button className="icon-btn notification-btn" onClick={handleNotificationClick}>
                <Bell size={26} />
                {notifications > 0 && <span className="badge">{notifications}</span>}
            </button>

            {isNotifOpen && (
                <div className={`notification-modal ${viewAllMode ? 'expanded' : ''}`}>
                    <div className="notif-header">
                        <h4>{viewAllMode ? 'All Notifications' : 'Recent Notifications'}</h4>
                        {notifications > 0 && !viewAllMode && <span className="notif-count">{notifications} New</span>}
                    </div>

                    <div className={`notif-body ${viewAllMode ? 'expanded-body' : ''}`}>
                        {loadingNotifs ? (
                            <div className="notif-empty">
                                <div className="spinner" style={{ width: '24px', height: '24px', borderWidth: '3px' }}></div>
                            </div>
                        ) : notifList.length === 0 ? (
                            <div className="notif-empty">
                                <Bell size={32} className="empty-bell" />
                                <p>{viewAllMode ? 'Inbox is empty' : 'No new messages'}</p>
                            </div>
                        ) : (
                            <ul className="notif-list">
                                {notifList.map((notif) => {
                                    const isJoinRequest = notif.type === 'GROUP_JOIN_REQUEST' && notif.metadata?.userId;
                                    // We only hide buttons if the admin took action (ACKNOWLEDGED), not just because they saw it (isRead).
                                    const isActioned = actionedNotifs.includes(notif._id) || notif.status === 'ACKNOWLEDGED';

                                    return (
                                        <li key={notif._id} className={`notif-item ${!notif.isRead && !actionedNotifs.includes(notif._id) ? 'unread' : ''}`}>
                                            <div className="notif-icon">
                                                {getNotificationIcon(notif.type)}
                                            </div>
                                            <div className="notif-content">
                                                <p className="notif-title">{notif.title}</p>
                                                <p className="notif-desc">{notif.body}</p>
                                                <span className="notif-time">
                                                    {new Date(notif.createdAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </span>

                                                {/* --- INLINE ACTION BUTTONS --- */}
                                                {isJoinRequest && !isActioned && (
                                                    <div className="notif-inline-actions">
                                                        <button
                                                            className="inline-btn approve-btn"
                                                            onClick={(e) => handleJoinAction(e, notif, 'APPROVE')}
                                                            disabled={actionLoadingId === notif._id}
                                                        >
                                                            {actionLoadingId === notif._id ? '...' : <><Check size={14} /> Approve</>}
                                                        </button>
                                                        <button
                                                            className="inline-btn reject-btn"
                                                            onClick={(e) => handleJoinAction(e, notif, 'REJECT')}
                                                            disabled={actionLoadingId === notif._id}
                                                        >
                                                            {actionLoadingId === notif._id ? '...' : <><X size={14} /> Reject</>}
                                                        </button>
                                                    </div>
                                                )}

                                                {/* --- SUCCESS INDICATOR --- */}
                                                {isJoinRequest && actionedNotifs.includes(notif._id) && (
                                                    <div className="notif-action-success">
                                                        <CheckCircle size={14} /> Action completed
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {!viewAllMode ? (
                        <div className="notif-footer" onClick={() => fetchAllNotifications(1)}>
                            View all notifications
                        </div>
                    ) : (
                        <div className="notif-pagination-footer">
                            <button
                                className="page-btn"
                                disabled={currentPage === 1 || loadingNotifs}
                                onClick={() => fetchAllNotifications(currentPage - 1)}
                            >
                                <ChevronLeft size={16} /> Prev
                            </button>
                            <span className="page-info">Page {currentPage} of {totalPages || 1}</span>
                            <button
                                className="page-btn"
                                disabled={currentPage === totalPages || totalPages === 0 || loadingNotifs}
                                onClick={() => fetchAllNotifications(currentPage + 1)}
                            >
                                Next <ChevronRight size={16} />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* --- Info Modal for Action Feedback --- */}
            {infoModal.show && (
                <div className="notif-modal-overlay" id="notif-modal-overlay">
                    <div className="notif-modal-card">
                        <div className={`notif-icon-wrapper ${infoModal.isError ? 'bg-red-light text-red' : 'bg-green-light text-green'}`}>
                            {infoModal.isError ? <XCircle size={36} /> : <CheckCircle size={36} />}
                        </div>
                        <h3>{infoModal.title}</h3>
                        <p>{infoModal.message}</p>
                        <button
                            className={`notif-btn-full ${infoModal.isError ? 'btn-danger-fill' : 'btn-success-fill'}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setInfoModal({ show: false, title: '', message: '', isError: false });
                            }}
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BellNotification;